import { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  EditOutlined as EditOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  PhotoCameraOutlined as PhotoCameraOutlinedIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNotify, useRefresh } from 'react-admin';
import PropTypes from 'prop-types';
import roles from '../../../constants/roles';
import dataProvider from '../../../providers/dataProvider';
import uploadService from '../../../utils/upload';

const initialsFor = (record) => {
  const first = record?.firstName?.[0] || '';
  const last = record?.lastName?.[0] || '';
  const initials = `${first}${last}`.trim();
  if (initials) return initials.toUpperCase();
  return (record?.email || '?')[0].toUpperCase();
};

const pictureUrl = (record) => {
  if (record?.src) return record.src;
  if (record?.picture) {
    return `${process.env.REACT_APP_IMAGES_FILE_HOST}/fit-in/400x400/${record.picture}`;
  }
  return null;
};

const focusGrantInput = () => {
  const el = document.querySelector('#program-access-grant input');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus();
  }
};

const ShowGrid = ({ record, permissions }) => {
  const refresh = useRefresh();
  const raNotify = useNotify();
  const notify = useCallback((message, type = 'info') => raNotify(message, { type }), [raNotify]);
  const fileInputRef = useRef(null);

  const [loginLinkDialog, setLoginLinkDialog] = useState(false);
  const [loginLink, setLoginLink] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);

  const [editingName, setEditingName] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isAdmin = permissions === roles.superAdmin || permissions === roles.admin;

  const copyToClipboard = useCallback(
    async (text) => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          notify('Login link copied to clipboard!');
          return;
        }
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        notify(
          successful ? 'Login link copied to clipboard!' : 'Copy failed',
          successful ? 'info' : 'warning',
        );
      } catch (_) {
        notify('Copy failed - please copy manually', 'warning');
      }
    },
    [notify],
  );

  const generateLoginLink = useCallback(async () => {
    if (generatingLink || !record?.id) return;
    setGeneratingLink(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      const res = await fetch(`${apiUrl}/admin/users/${record.id}/generate-login-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to generate login link');
      const data = await res.json();
      const url = data.loginUrl || '';
      setLoginLink(url);
      setLoginLinkDialog(true);
      if (url) copyToClipboard(url);
    } catch (err) {
      notify('Failed to generate login link', 'error');
    } finally {
      setGeneratingLink(false);
    }
  }, [record, generatingLink, copyToClipboard, notify]);

  // Resolves to true when the save went through, so callers can leave edit mode only then.
  const saveUser = useCallback(
    async (patch, successMessage) => {
      if (!record?.id) return false;
      setSaving(true);
      try {
        await dataProvider.update('users', { id: record.id, data: patch });
        if (successMessage) notify(successMessage, 'success');
        refresh();
        return true;
      } catch (err) {
        notify(typeof err === 'string' ? err : err?.message || 'Save failed', 'error');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [record, refresh, notify],
  );

  const startEditingName = () => {
    setFirstName(record.firstName || '');
    setLastName(record.lastName || '');
    setEditingName(true);
  };

  const cancelEditingName = () => {
    setEditingName(false);
    setFirstName('');
    setLastName('');
  };

  const submitName = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      notify('First and last name are required', 'error');
      return;
    }
    await saveUser({ firstName: firstName.trim(), lastName: lastName.trim() });
    setEditingName(false);
  };

  const startEditingEmail = () => {
    setEmail(record.email || '');
    setEditingEmail(true);
  };

  const cancelEditingEmail = () => {
    setEditingEmail(false);
    setEmail('');
  };

  const submitEmail = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      notify('Email is required', 'error');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      notify('Please enter a valid email address', 'error');
      return;
    }
    // The API moves the SuperTokens login to the new address inside the same transaction and
    // then re-keys the Bento contact, so a rejection here (e.g. address already in use) means
    // nothing changed anywhere — keep the field open so the admin can correct it.
    const saved = await saveUser(
      { email: trimmed },
      'Email updated — login and Bento contact moved to the new address',
    );
    if (saved) setEditingEmail(false);
  };

  const toggleApAccess = async (event) => {
    await saveUser({ apAccess: event.target.checked });
  };

  const onPickFile = () => {
    fileInputRef.current?.click();
  };

  const onFileChosen = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadService(formData);
      const fileName = res?.data?.fileName;
      if (!fileName) throw new Error('Upload returned no filename');
      await saveUser({ picture: fileName });
    } catch (err) {
      notify(typeof err === 'string' ? err : err?.message || 'Photo upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  if (!record) return null;

  const displayName =
    [record.firstName, record.lastName].filter(Boolean).join(' ') || record.email || 'Customer';
  const joined = record.createdAt ? format(new Date(record.createdAt), 'MMM d, yyyy') : null;
  const picture = pictureUrl(record);

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            variant="h1"
            sx={{ fontSize: '2rem', fontWeight: 600, lineHeight: 1.3, mb: 0.5 }}
          >
            Customer detail
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customer ID {record.id}
            {joined && ` · Joined ${joined}`}
          </Typography>
        </Box>
        {isAdmin && (
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={generateLoginLink}
              disabled={generatingLink}
            >
              {generatingLink ? 'Generating…' : 'Generate Login Link'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={focusGrantInput}
            >
              Grant Program Access
            </Button>
          </Stack>
        )}
      </Box>

      <Paper sx={{ p: 4, mb: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '200px 1fr' },
            gap: 4,
            alignItems: 'center',
          }}
        >
          <Box sx={{ justifySelf: { xs: 'center', md: 'start' } }}>
            <Box
              role={isAdmin ? 'button' : undefined}
              tabIndex={isAdmin ? 0 : undefined}
              onClick={isAdmin && !uploading ? onPickFile : undefined}
              onKeyDown={(e) => {
                if (isAdmin && !uploading && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onPickFile();
                }
              }}
              sx={{
                width: 200,
                height: 200,
                borderRadius: 1,
                background: picture
                  ? `url(${picture}) center/cover no-repeat`
                  : 'linear-gradient(135deg, #F0F2F5 0%, #DADEE3 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                cursor: isAdmin ? 'pointer' : 'default',
                outline: 'none',
                transition: 'box-shadow 150ms',
                '&:hover .photo-overlay, &:focus-visible .photo-overlay': {
                  opacity: isAdmin ? 1 : 0,
                },
                '&:focus-visible': {
                  boxShadow: 3,
                },
              }}
              aria-label={
                isAdmin
                  ? `Change photo for ${displayName}`
                  : picture
                    ? `${displayName} photo`
                    : `${displayName} initials`
              }
            >
              {!picture && (
                <Typography sx={{ fontSize: 64, fontWeight: 600, color: 'grey.500' }}>
                  {initialsFor(record)}
                </Typography>
              )}
              {isAdmin && (
                <Box
                  className="photo-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 1,
                    backgroundColor: 'rgba(33, 38, 45, 0.6)',
                    color: 'common.white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    opacity: 0,
                    transition: 'opacity 150ms',
                  }}
                >
                  {uploading ? (
                    <CircularProgress size={28} sx={{ color: 'common.white' }} />
                  ) : (
                    <>
                      <PhotoCameraOutlinedIcon />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Change photo
                      </Typography>
                    </>
                  )}
                </Box>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={onFileChosen}
              />
            </Box>
          </Box>

          <Stack spacing={2}>
            {editingName ? (
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <TextField
                  size="small"
                  label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={saving}
                  autoFocus
                />
                <TextField
                  size="small"
                  label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={saving}
                />
                <Button variant="contained" size="small" onClick={submitName} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button size="small" onClick={cancelEditingName} disabled={saving}>
                  Cancel
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="h2"
                  sx={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2 }}
                >
                  {displayName}
                </Typography>
                {isAdmin && (
                  <Tooltip title="Edit name">
                    <IconButton size="small" onClick={startEditingName} aria-label="Edit name">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 2, md: 4 },
                rowGap: 1.5,
              }}
            >
              <Box>
                <Typography
                  component="div"
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    mb: 0.5,
                  }}
                >
                  Email
                </Typography>
                {editingEmail ? (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <TextField
                      size="small"
                      type="email"
                      label="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={saving}
                      autoFocus
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={submitEmail}
                      disabled={saving}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button size="small" onClick={cancelEditingEmail} disabled={saving}>
                      Cancel
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                        fontSize: '0.875rem',
                      }}
                    >
                      {record.email}
                    </Typography>
                    {isAdmin && (
                      <Tooltip title="Edit email">
                        <IconButton
                          size="small"
                          onClick={startEditingEmail}
                          aria-label="Edit email"
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                )}
              </Box>
              <MetaField label="Date Joined" value={joined || '—'} />
              <MetaField
                label="MN Member ID"
                value={record.mnMemberId || '—'}
                muted={!record.mnMemberId}
              />
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={1}>
              {record.questionnaireComplete ? (
                <Chip
                  variant="outlined"
                  size="small"
                  label="Intro Questionnaire Complete"
                  sx={{ borderColor: 'success.main', color: 'success.dark' }}
                />
              ) : (
                <Chip
                  size="small"
                  color="warning"
                  variant="filled"
                  label="Intro Questionnaire Incomplete"
                />
              )}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pl: 0.5,
                  pr: 1,
                  py: 0.25,
                  borderRadius: 6,
                  border: 1,
                  borderColor: 'grey.300',
                }}
              >
                <Switch
                  size="small"
                  checked={!!record.apAccess}
                  onChange={toggleApAccess}
                  disabled={!isAdmin || saving}
                  slotProps={{ input: { 'aria-label': 'Toggle AP access' } }}
                />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  AP Access · {record.apAccess ? 'Yes' : 'No'}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      <Dialog
        open={loginLinkDialog}
        onClose={() => setLoginLinkDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Customer Login Link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Use this link in an <b>incognito window</b> to login as customer. The link expires in 15
            minutes.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: 1,
              borderColor: 'grey.300',
              borderRadius: 1,
              p: 1,
              backgroundColor: 'grey.100',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                flexGrow: 1,
                wordBreak: 'break-all',
                mr: 1,
                fontFamily: 'monospace',
                fontSize: 12,
              }}
            >
              {typeof loginLink === 'string' ? loginLink : 'No link generated'}
            </Typography>
            <IconButton
              size="small"
              title="Copy to clipboard"
              onClick={() => copyToClipboard(loginLink)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginLinkDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const MetaField = ({ label, value, mono, muted }) => (
  <Box>
    <Typography
      component="div"
      variant="caption"
      sx={{
        display: 'block',
        color: 'text.secondary',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontFamily: mono ? 'ui-monospace, "SF Mono", Menlo, monospace' : 'inherit',
        fontSize: mono ? '0.875rem' : '0.9375rem',
        color: muted ? 'text.disabled' : 'text.primary',
      }}
    >
      {value}
    </Typography>
  </Box>
);

MetaField.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  mono: PropTypes.bool,
  muted: PropTypes.bool,
};

MetaField.defaultProps = {
  mono: false,
  muted: false,
};

ShowGrid.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    apAccess: PropTypes.bool,
    questionnaireComplete: PropTypes.bool,
    mnMemberId: PropTypes.string,
    picture: PropTypes.string,
    src: PropTypes.string,
  }),
  permissions: PropTypes.string,
};

ShowGrid.defaultProps = {
  record: null,
  permissions: undefined,
};

export default ShowGrid;
